import React from 'react';
import { FormCombobox } from '@/components/custom/form';

/**
 * Wrapper universal compatível com react-select + Formik
 */
export default function FormComboboxWrapper({ name, form, options = [], value: externalValue, onChange, isMulti, multiple, placeholder = 'Selecione...', ...props }) {
  const isMultiple = isMulti ?? multiple ?? false;

  /**
   * 🔒 SAFE GET FORM VALUE
   */
  const formValue = name && form?.values ? form.values[name] : undefined;

  const currentValue = externalValue !== undefined ? externalValue : formValue;

  /**
   * 🔁 NORMALIZE OPTIONS
   */
  const normalizeOptions = React.useCallback((opts = []) => {
    if (!Array.isArray(opts)) return [];

    return opts
      .map(opt => {
        if (!opt) return null;

        // já está no formato correto
        if (opt.label !== undefined && opt.value !== undefined) {
          return opt;
        }

        return {
          value: opt.id ?? opt.value ?? null,
          label: opt.title ?? opt.name ?? opt.label ?? (opt.id !== undefined ? String(opt.id) : '')
        };
      })
      .filter(Boolean);
  }, []);

  /**
   * 🔁 NORMALIZE VALUE
   */
  const normalizeValue = React.useCallback(
    value => {
      if (isMultiple) {
        if (!Array.isArray(value)) return [];

        return value
          .map(v => {
            if (!v) return null;

            if (v.label !== undefined && v.value !== undefined) {
              return v.value;
            }

            return v.id ?? v.value ?? null;
          })
          .filter(v => v !== null && v !== undefined);
      }

      if (!value) return null;

      if (value.label !== undefined && value.value !== undefined) {
        return value.value;
      }

      return value.id ?? value.value ?? null;
    },
    [isMultiple]
  );

  /**
   * 🔁 PARSE CHANGE (OUTPUT PADRÃO react-select)
   */
  const parseOnChange = React.useCallback(
    val => {
      if (isMultiple) {
        if (!Array.isArray(val)) return [];

        return val.map(v => ({
          value: v,
          label: options.find(o => (o.id ?? o.value) === v)?.title ?? options.find(o => (o.id ?? o.value) === v)?.name ?? String(v)
        }));
      }

      if (val === null || val === undefined) return null;

      return {
        value: val,
        label: options.find(o => (o.id ?? o.value) === val)?.title ?? options.find(o => (o.id ?? o.value) === val)?.name ?? String(val)
      };
    },
    [isMultiple, options]
  );

  const normalizedOptions = React.useMemo(() => normalizeOptions(options), [options, normalizeOptions]);

  const normalizedValue = React.useMemo(() => normalizeValue(currentValue), [currentValue, normalizeValue]);

  /**
   * 🔥 HANDLE CHANGE UNIVERSAL
   */
  const handleChange = val => {
    const parsed = parseOnChange(val);

    // Formik safe
    if (form && name && typeof form.setFieldValue === 'function') {
      form.setFieldValue(name, parsed);
    }

    // compat com react-select
    if (onChange) {
      onChange(parsed);
    }
  };

  /**
   * 🛡️ FAILSAFE (evita crash silencioso)
   */
  if (!Array.isArray(options)) {
    console.warn('[FormComboboxWrapper] options inválido:', options);
  }

  return (
    <FormCombobox
      options={normalizedOptions}
      value={normalizedValue}
      multiple={isMultiple}
      placeholder={placeholder}
      onValueChange={handleChange}
      {...props}
    />
  );
}
